// TODO: SPT:REMOVE

import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { createRequestSaga } from '@/redux/helpers';
import Api, { Mapper } from '../../api';
import {
  requestTeamPolygonsAsync,
  requestPolygonStatisticsAsync,
  requestMultiplePolygonStatisticsAsync,
  requestPolygonDispositionsAsync,
  requestPreviewAsync,
  requestCreateAsync,
  requestUpdateBoundaryAsync,
  requestUpdateRepsAsync,
  requestRemoveRepsAsync,
  requestActivateAsync,
  requestDeactivateAsync,
  requestDeletePolygonAsync,
  requestClearPolygonOutcomesAsync,
} from './actions';
import { requestRepsListAsync } from './../teams/actions';
import { userSelector } from '@/redux/auth';
import { addErrorAction } from '@/redux/errors';
import { addToastsAction } from '@/redux/toasts';

function* requestTeamPolygonsSaga({ payload }) {
  const { team_id, boundary, callback } = payload;
  try {
    const limit = 1000;
    const firstPage = yield call(
      Api.getTeamPolygons(payload.team_id),
      {
        page: 1,
        limit: limit,
        team_id: team_id,
        boundary: {
          'type': 'Polygon',
          'coordinates': boundary,
        },
      },
    );

    // If there are more pages of data, they exceeded the viewable threshold and an toast should be displayed
    if (firstPage.data.current_page !== firstPage.data.last_page) {
      yield put(addToastsAction(Mapper.exceededPolygonLimitToastData()));
    }

    // Only had 1 page of data to return
    yield call(callback(firstPage?.data?.data ?? []));
  } catch (error) {
    yield put(addErrorAction({ errKey: 'teamPolygons', error: error.response.data.errors }));
  }
}

function* requestPolygonStatisticsSaga({ payload }) {
  const { polygon_id } = payload;

  const response = yield call(
    Api.getPolygonStatistics(polygon_id),
  );

  const data = Mapper.getPolygonStatData(response);

  yield put(requestPolygonStatisticsAsync.success({ data, polygonId: polygon_id }));
}

function* requestMultiplePolygonStatisticsSaga({ payload }) {
  const { polygon_ids } = payload;

  yield all(polygon_ids.map((polygon_id) =>
    call(function* () {
      const response = yield call(Api.getPolygonStatistics(polygon_id));
      const data = Mapper.getPolygonStatData(response);
      yield put(requestPolygonStatisticsAsync.success({ data, polygonId: polygon_id }));
    })));
}

function* requestPolygonDispositionsSaga({ payload }) {
  const { id, type, page, limit, sort_by, order, search } = payload;
  try {
    if (type === 'area') {
      const { data } = yield call(
        Api.getAreaDispositions(id),
        { page, limit, sort_by, order, search },
      );
      yield put(requestPolygonDispositionsAsync.success(Mapper.getPolygonDispositions(data)));
    } else {
      const { data } = yield call(
        Api.getTeamDispositions(id),
        { page, limit, sort_by, order },
      );

      yield put(requestPolygonDispositionsAsync.success(Mapper.getPolygonDispositions(data)));
    }
  } catch (error) {
    yield put(addErrorAction({ errKey: 'polygonDispositions', error: error.response.data.errors }));
  }
}

function* requestPreviewSaga({ payload }) {
  const { boundary, team_id } = payload;
  try {
    const response = yield call(
      Api.preview,
      { boundary, team_id },
    );

    const data = Mapper.getPolygonStatData(response);

    yield put(requestPreviewAsync.success({ data }));
  } catch (error) {
    yield put(addErrorAction({ errKey: 'polygonPreview', error: error.response.data.errors }));
  }
}

function* requestCreateSaga({ payload }) {
  const { team_id, reps, boundary, callback } = payload;

  try {
    const { data } = yield call(
      Api.createPolygon,
      {
        team_id,
        reps,
        boundary,
      },
    );
    yield put(addToastsAction(Mapper.getCreatePolygonToastData(data)));
    yield loadReps(team_id);
    yield call(callback, data?.polygon, []);
  } catch (error) {
    const errorResponseData = error?.response?.data;

    if (errorResponseData?.data?.polygon) {
      const { data } = errorResponseData ?? {};
      yield put(addToastsAction(Mapper.getCreatePolygonToastData(data)));
      yield loadReps(team_id);
      yield call(callback, data?.polygon, Mapper.getUnlicensedRepsForPolygon(data));
    } else {
      yield put(addErrorAction({ errKey: 'createPolygon', error: errorResponseData?.errors }));
    }
  }
}

function* loadReps(team_id) {
  try {
    const teamMap = yield call(
      Api.getRepsList(team_id),
    );

    yield put(requestRepsListAsync.success(teamMap));
  } catch (error) {
    yield put(addErrorAction({ errKey: 'teamReps', error: error.response.data.errors }));
  }
}

function* requestUpdateBoundarySaga({ payload }) {
  try {
    const { polygon_id, boundary, team_id, callback } = payload;

    const response = yield call(
      Api.updateBoundary(polygon_id),
      { boundary },
    );
    const data = Mapper.getUpdatedPolygonStatData(response);
    yield put(requestPolygonStatisticsAsync.success({ data, polygonId: polygon_id }));
    yield loadReps(team_id);
    yield call(callback());
  } catch (error) {
    yield put(addErrorAction({ errKey: 'updateBoundary', error: error.response.data.errors }));
  }
}

function* requestUpdateRepsSaga({ payload }) {
  const { polygon_ids, team_id, reps, callback, assignUnlicensedReps } = payload;
  try {
    const body = {
      ...(typeof team_id !== 'undefined' && { team_id: team_id }),
      ...(typeof reps !== 'undefined' && { reps: reps }),
      ...(assignUnlicensedReps && { accept_unlicensed_reps: true }),
    };

    const { data } = yield call(
      Api.assignRepsToPolygon,
      { polygon_ids, ...body },
    );

    if (reps.length) {
      yield put(addToastsAction(Mapper.getAssignPolygonData(data, reps)));
      yield loadReps(team_id);
    }
    yield call(callback, Mapper.getUnlicensedRepsForPolygons(data));
  } catch (error) {
    const errorResponseData = error?.response?.data;

    if (errorResponseData?.errors) {
      yield put(addErrorAction({ errKey: 'updateReps', error: errorResponseData?.errors }));
    } else {
      const licensingRestrictionsInformation = Mapper.getUnlicensedRepsForPolygons(errorResponseData?.data);
      yield put(addToastsAction(Mapper.getAssignPolygonData(errorResponseData?.data, reps)));
      yield loadReps(team_id);
      yield call(callback, licensingRestrictionsInformation);
    }
  }
}

function* requestRemoveRepsSaga({ payload }) {
  const { polygon_ids, team_id, callback } = payload;

  try {
    const { data } = yield call(
      Api.unassignRepsFromPolygons,
      { polygon_ids },
    );
    const unassignedPolygons = Mapper.getPolygonIds(data);
    yield put(addToastsAction(Mapper.getUnassignPolygonData(data)));
    yield loadReps(team_id);
    yield call(callback(unassignedPolygons));
  } catch (error) {
    const unassignedPolygons = Mapper.getPolygonIds(error.response.data);
    yield put(addToastsAction(Mapper.getUnassignPolygonData(error.response.data)));
    yield call(callback(unassignedPolygons));
  }
}

function* requestClearPolygonOutcomesSaga({ payload }) {
  try {
    const { team_id, polygon_ids, callback } = payload;
    const user = yield select(userSelector);

    yield call(
      Api.clearPolygonOutcomes,
      { polygon_ids, season_id: user?.sales_season_id },
    );
    yield put(addToastsAction(Mapper.getClearOutcomesToastData()));
    yield loadReps(team_id);
    yield call(callback());
  } catch (error) {
    yield put(addErrorAction({ errKey: 'clearOutcomes', error: error.response.data.errors }));
  }
}

function* requestActivateSaga({ payload }) {
  const { polygon_id, team_id, reps, callback } = payload;

  try {
    const activateRequest = call(Api.activate(polygon_id));
    const assignRepsRequest = reps.length
      ? call(
        Api.assignRepsToPolygon,
        { reps, team_id, polygon_ids: [polygon_id] },
      )
      : null;

    const [activateResponse] = yield all([activateRequest, assignRepsRequest]);

    yield put(addToastsAction(Mapper.getActivatePolygonToastData(activateResponse?.data)));
    yield loadReps(team_id);
    yield call(callback);
  } catch (error) {
    const errorResponseData = error?.response?.data;

    if (errorResponseData?.errors) {
      yield put(addErrorAction({ errKey: 'activatePolygon', error: errorResponseData?.errors }));
    }
  }
}

function* requestDeactivateSaga({ payload }) {
  const { polygon_ids, team_id, callback } = payload;

  try {
    const { data } = yield call(
      Api.deactivatePolygons,
      { polygon_ids },
    );
    const deactivatedPolygons = Mapper.getSuccessfulPolygonIds(data);
    yield put(addToastsAction(Mapper.getDeactivatePolygonToastsData(data)));
    yield loadReps(team_id);
    yield call(callback(deactivatedPolygons));
  } catch (error) {
    const deactivatedPolygons = Mapper.getSuccessfulPolygonIds(error.response?.data?.data);
    yield put(addToastsAction(Mapper.getDeactivatePolygonToastsData(error.response?.data?.data)));
    yield call(callback(deactivatedPolygons));
  }
}

function* requestDeletePolygonSaga({ payload }) {
  const { team_id, polygon_ids, callback } = payload;

  try {
    const { data } = yield call(
      Api.deletePolygons,
      { polygon_ids },
    );
    const deletedPolygons = Mapper.getSuccessfulPolygonIds(data);
    yield put(addToastsAction(Mapper.getDeletePolygonToastsData(data)));
    yield loadReps(team_id);
    yield call(callback(deletedPolygons));
  } catch (error) {
    const deletedPolygons = Mapper.getSuccessfulPolygonIds(error.response?.data?.data);
    yield put(addToastsAction(Mapper.getDeletePolygonToastsData(error.response?.data?.data)));
    yield call(callback(deletedPolygons));
  }
}

export function* polygonsActionWatcher() {
  yield takeLatest(
    requestTeamPolygonsAsync.request.type,
    createRequestSaga(requestTeamPolygonsSaga, {
      keyNew: 'teamPolygons',
      errKey: 'teamPolygons',
      write: false,
    }),
  );

  yield takeLatest(
    requestPolygonStatisticsAsync.request.type,
    createRequestSaga(requestPolygonStatisticsSaga, {
      keyNew: 'polygonStatistics',
      errKey: 'polygonStatistics',
      write: false,
    }),
  );

  yield takeLatest(
    requestMultiplePolygonStatisticsAsync.request.type,
    createRequestSaga(requestMultiplePolygonStatisticsSaga, {
      keyNew: 'polygonStatistics',
      errKey: 'polygonStatistics',
      write: false,
    }),
  );

  yield takeLatest(
    requestPolygonDispositionsAsync.request.type,
    createRequestSaga(requestPolygonDispositionsSaga, {
      keyNew: 'polygonDispositions',
      errKey: 'polygonDispositions',
      write: false,
    }),
  );

  yield takeLatest(
    requestPreviewAsync.request.type,
    createRequestSaga(requestPreviewSaga, {
      keyNew: 'polygonPreview',
      errKey: 'polygonPreview',
      write: false,
    }),
  );

  yield takeLatest(
    requestCreateAsync.request.type,
    createRequestSaga(requestCreateSaga, {
      keyNew: 'createPolygon',
      errKey: 'createPolygon',
      write: false,
    }),
  );

  yield takeLatest(
    requestUpdateBoundaryAsync.request.type,
    createRequestSaga(requestUpdateBoundarySaga, {
      keyNew: 'updateBoundary',
      errKey: 'updateBoundary',
      write: false,
    }),
  );

  yield takeLatest(
    requestUpdateRepsAsync.request.type,
    createRequestSaga(requestUpdateRepsSaga, {
      keyNew: 'updatePolygonReps',
      errKey: 'updatePolygonReps',
      write: false,
    }),
  );

  yield takeLatest(
    requestRemoveRepsAsync.request.type,
    createRequestSaga(requestRemoveRepsSaga, {
      keyNew: 'removePolygonReps',
      errKey: 'removePolygonReps',
      write: false,
    }),
  );

  yield takeLatest(
    requestDeletePolygonAsync.request.type,
    createRequestSaga(requestDeletePolygonSaga, {
      keyNew: 'deletePolygon',
      errKey: 'deletePolygon',
      write: false,
    }),
  );

  yield takeLatest(
    requestClearPolygonOutcomesAsync.request.type,
    createRequestSaga(requestClearPolygonOutcomesSaga, {
      keyNew: 'clearOutcomes',
      errKey: 'clearOutcomes',
      write: false,
    }),
  );

  yield takeLatest(
    requestActivateAsync.request.type,
    createRequestSaga(requestActivateSaga, {
      keyNew: 'activate',
      errKey: 'activate',
      write: false,
    }),
  );

  yield takeLatest(
    requestDeactivateAsync.request.type,
    createRequestSaga(requestDeactivateSaga, {
      keyNew: 'deactivate',
      errKey: 'deactivate',
      write: false,
    }),
  );
}
